import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { 
  Building2,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  MapPin,
  DollarSign,
  Clock,
  Users,
  BarChart3,
  Navigation,
  Search,
  CheckCircle,
  X
} from "lucide-react";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Circle, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom site icon
const siteIcon = new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="background: #059669; border: 3px solid white; border-radius: 50%; width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 8px rgba(0,0,0,0.3);">
    <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
  </div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 36],
  popupAnchor: [0, -36]
});

// Map click handler component
function MapClickHandler({ onLocationSelect }) {
  useMapEvents({
    click: (e) => {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

// Map center updater
function MapCenterUpdater({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.flyTo(center, 15, { duration: 1 });
    }
  }, [center, map]);
  return null;
}

export default function AdminProjects() {
  const { authAxios } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const isValidCoord = (lat, lng) => {
    const a = parseFloat(lat);
    const b = parseFloat(lng);
    return Number.isFinite(a) && Number.isFinite(b) && Math.abs(a) <= 90 && Math.abs(b) <= 180;
  };

  const [dialogOpen, setDialogOpen] = useState(false);
  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectSummary, setProjectSummary] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    location: '',
    latitude: '',
    longitude: '',
    radius_meters: 150,
    geofencing_enabled: true,
    is_active: true
  });
  const [submitting, setSubmitting] = useState(false);
  
  // Location search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [mapCenter, setMapCenter] = useState([49.15, -121.95]); // Default to Chilliwack area
  const [showMap, setShowMap] = useState(false);
  const searchTimeoutRef = useRef(null);

  const fetchProjects = useCallback(async () => {
    try {
      const res = await authAxios.get('/projects');
      setProjects(res.data);
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const resetForm = () => {
    setFormData({ 
      name: '', 
      description: '', 
      location: '', 
      latitude: '',
      longitude: '',
      radius_meters: 150,
      geofencing_enabled: true,
      is_active: true 
    });
    setEditingProject(null);
    setSearchQuery('');
    setSearchResults([]);
    setShowMap(false);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (project) => {
    setEditingProject(project);
    setFormData({
      name: project.name,
      description: project.description || '',
      location: project.location || '',
      latitude: project.latitude || '',
      longitude: project.longitude || '',
      radius_meters: project.radius_meters || 150,
      geofencing_enabled: project.geofencing_enabled !== false,
      is_active: project.is_active
    });
    setSearchQuery(project.location || '');
    if (isValidCoord(project.latitude, project.longitude)) {
      setMapCenter([project.latitude, project.longitude]);
      setShowMap(true);
    }
    setDialogOpen(true);
  };

  // Search for location using Nominatim (OpenStreetMap)
  const searchLocation = async (query) => {
    if (!query || query.length < 3) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&countrycodes=ca,us`
      );
      const data = await response.json();
      setSearchResults(data);
    } catch (error) {
      console.error('Search failed:', error);
      toast.error('Location search failed');
    } finally {
      setSearching(false);
    }
  };

  // Debounced search
  const handleSearchChange = (value) => {
    setSearchQuery(value);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      searchLocation(value);
    }, 500);
  };

  // Select a search result
  const handleSelectLocation = (result) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    setFormData({
      ...formData,
      location: result.display_name,
      latitude: lat,
      longitude: lng
    });
    setSearchQuery(result.display_name);
    setSearchResults([]);
    setMapCenter([lat, lng]);
    setShowMap(true);
    toast.success('Location selected! You can adjust by clicking on the map.');
  };

  // Handle map click to set location
  const handleMapClick = async (lat, lng) => {
    setFormData(prev => ({ ...prev, latitude: lat, longitude: lng }));
    
    // Reverse geocode to get address
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
      );
      const data = await response.json();
      if (data.display_name) {
        setFormData(prev => ({
          ...prev,
          location: data.display_name
        }));
        setSearchQuery(data.display_name);
      }
    } catch (error) {
      console.error('Reverse geocode failed:', error);
    }
    
    toast.success('Location updated!');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const submitData = {
      ...formData,
      latitude: formData.latitude ? parseFloat(formData.latitude) : null,
      longitude: formData.longitude ? parseFloat(formData.longitude) : null,
      radius_meters: parseInt(formData.radius_meters) || 150
    };

    try {
      if (editingProject) {
        await authAxios.put(`/projects/${editingProject.id}`, submitData);
        toast.success('Project updated!');
      } else {
        await authAxios.post('/projects', submitData);
        toast.success('Project created!');
      }
      setDialogOpen(false);
      resetForm();
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    
    try {
      await authAxios.delete(`/projects/${projectId}`);
      toast.success('Project deleted');
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete project');
    }
  };

  const viewProjectSummary = async (project) => {
    setSelectedProject(project);
    try {
      const res = await authAxios.get(`/projects/${project.id}/summary`);
      setProjectSummary(res.data);
      setSummaryDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load project summary');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-projects">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-stone-900 font-heading">Projects / Job Sites</h1>
          <p className="text-stone-500 mt-1">Manage work locations with geofencing</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button onClick={openCreateDialog} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="add-project-btn">
              <Plus className="w-5 h-5 mr-2" />
              Add Project
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">{editingProject ? 'Edit Project' : 'Add New Project'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Project Name *</Label>
                <Input 
                  id="name" 
                  value={formData.name} 
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
                  required 
                  data-testid="project-name-input" 
                  className="bg-stone-50 border-stone-200" 
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea 
                  id="description" 
                  value={formData.description} 
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })} 
                  data-testid="project-description-input" 
                  className="bg-stone-50 border-stone-200" 
                  rows={2} 
                />
              </div>

              {/* Location Search Section */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="w-4 h-4 text-emerald-600" />
                  <span className="font-medium text-sm">Location & Geofencing</span>
                </div>
                
                <div className="space-y-2">
                  <Label>Search Location</Label>
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                    <Input
                      value={searchQuery}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      placeholder="Search for an address or place..."
                      data-testid="location-search-input"
                      className="pl-10 bg-stone-50 border-stone-200"
                    />
                    {searching && (
                      <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-emerald-600 animate-spin" />
                    )}
                  </div>
                  
                  {/* Search Results */}
                  {searchResults.length > 0 && (
                    <div className="border rounded-lg bg-white shadow-lg max-h-48 overflow-y-auto">
                      {searchResults.map((result, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => handleSelectLocation(result)}
                          className="w-full px-3 py-2 text-left hover:bg-emerald-50 flex items-start gap-2 border-b last:border-b-0"
                        >
                          <MapPin className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                          <span className="text-sm">{result.display_name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Map Preview */}
                {showMap && isValidCoord(formData.latitude, formData.longitude) && (() => {
                  const lat = parseFloat(formData.latitude);
                  const lng = parseFloat(formData.longitude);
                  return (
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Click map to adjust location</Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowMap(false)}
                        data-testid="hide-map-btn"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Hide Map
                      </Button>
                    </div>
                    <div className="h-64 rounded-lg overflow-hidden border">
                      <MapContainer
                        center={mapCenter}
                        zoom={15}
                        style={{ height: '100%', width: '100%' }}
                        key={`${mapCenter[0]}-${mapCenter[1]}`}
                      >
                        <TileLayer
                          attribution='&copy; OpenStreetMap'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <MapClickHandler onLocationSelect={handleMapClick} />
                        <MapCenterUpdater center={mapCenter} />
                        <Marker position={[lat, lng]} icon={siteIcon} />
                        {formData.geofencing_enabled && (
                          <Circle
                            center={[lat, lng]}
                            radius={parseInt(formData.radius_meters) || 150}
                            pathOptions={{
                              color: '#059669',
                              fillColor: '#059669',
                              fillOpacity: 0.2,
                              weight: 2
                            }}
                          />
                        )}
                      </MapContainer>
                    </div>
                    <p className="text-xs text-stone-500 flex items-center gap-1" data-testid="map-coords">
                      <MapPin className="w-3 h-3" /> {lat.toFixed(6)}, {lng.toFixed(6)}
                    </p>
                  </div>
                  );
                })()}

                {!showMap && isValidCoord(formData.latitude, formData.longitude) && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    data-testid="show-map-btn"
                    onClick={() => {
                      setMapCenter([parseFloat(formData.latitude), parseFloat(formData.longitude)]);
                      setShowMap(true);
                    }}
                  >
                    <MapPin className="w-4 h-4 mr-1" />
                    Show Map
                  </Button>
                )}

                {/* Coordinates Display */}
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-stone-500">Latitude</Label>
                    <Input 
                      type="number" 
                      step="any"
                      value={formData.latitude} 
                      onChange={(e) => setFormData({ ...formData, latitude: e.target.value })} 
                      className="bg-stone-50 border-stone-200 text-sm" 
                      placeholder="e.g., 49.2827"
                      data-testid="project-latitude-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-stone-500">Longitude</Label>
                    <Input 
                      type="number" 
                      step="any"
                      value={formData.longitude} 
                      onChange={(e) => setFormData({ ...formData, longitude: e.target.value })} 
                      className="bg-stone-50 border-stone-200 text-sm" 
                      placeholder="e.g., -123.1207"
                      data-testid="project-longitude-input"
                    />
                  </div>
                </div>

                {/* Geofence Settings */}
                <div className="mt-4 p-4 bg-stone-50 rounded-lg space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Switch 
                        checked={formData.geofencing_enabled} 
                        onCheckedChange={(checked) => setFormData({ ...formData, geofencing_enabled: checked })} 
                        data-testid="geofencing-toggle"
                      />
                      <Label className="text-sm font-medium">Enable Geofencing</Label>
                    </div>
                    {formData.geofencing_enabled && (
                      <Badge className="bg-emerald-100 text-emerald-700 font-mono">
                        {formData.radius_meters}m
                      </Badge>
                    )}
                  </div>
                  {formData.geofencing_enabled && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs text-stone-500">Geofence Radius</Label>
                        <span className="text-xs text-stone-400">50m — 500m</span>
                      </div>
                      <Slider
                        value={[formData.radius_meters]}
                        onValueChange={([val]) => setFormData({ ...formData, radius_meters: val })}
                        min={50}
                        max={500}
                        step={10}
                        className="w-full"
                        data-testid="radius-slider"
                      />
                      <p className="text-xs text-stone-500">
                        Employees must be within {formData.radius_meters}m of this location to clock in
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-3 border-t pt-4">
                <Switch 
                  checked={formData.is_active} 
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })} 
                />
                <Label>Active (available for clock-in)</Label>
              </div>
              
              <DialogFooter>
                <Button 
                  type="submit" 
                  disabled={submitting} 
                  className="bg-emerald-600 hover:bg-emerald-500 text-white w-full" 
                  data-testid="save-project-btn"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
                  ) : (
                    <><CheckCircle className="w-4 h-4 mr-2" />{editingProject ? 'Update Project' : 'Create Project'}</>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Projects Table */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <Building2 className="w-5 h-5 text-stone-400" />
            All Projects ({projects.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {projects.length === 0 ? (
            <div className="text-center py-12 text-stone-500">
              <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No projects yet. Create your first project to get started.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-stone-100">
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Project</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Location</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Geofencing</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Status</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {projects.map((project) => (
                    <TableRow key={project.id} className="border-stone-100 hover:bg-stone-50">
                      <TableCell>
                        <div>
                          <p className="font-medium">{project.name}</p>
                          {project.description && (
                            <p className="text-sm text-stone-500 truncate max-w-[200px]">{project.description}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {isValidCoord(project.latitude, project.longitude) ? (
                          <div className="flex items-center gap-1">
                            <MapPin className="w-4 h-4 text-emerald-600" />
                            <span className="text-sm text-stone-600 truncate max-w-[200px]">
                              {project.location || `${project.latitude.toFixed(4)}, ${project.longitude.toFixed(4)}`}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-stone-400">{project.location || 'No coordinates'}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {project.geofencing_enabled && isValidCoord(project.latitude, project.longitude) ? (
                          <Badge className="bg-emerald-100 text-emerald-700">
                            <Navigation className="w-3 h-3 mr-1" />
                            {project.radius_meters || 150}m
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-stone-400">Disabled</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={project.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-100 text-stone-600'}>
                          {project.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => viewProjectSummary(project)} title="View Summary">
                            <BarChart3 className="w-4 h-4 text-stone-500" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => openEditDialog(project)} title="Edit">
                            <Pencil className="w-4 h-4 text-stone-500" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(project.id)} title="Delete">
                            <Trash2 className="w-4 h-4 text-rose-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Project Summary Dialog */}
      <Dialog open={summaryDialogOpen} onOpenChange={setSummaryDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-emerald-600" />
              {selectedProject?.name}
            </DialogTitle>
          </DialogHeader>
          {projectSummary && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Card className="border-0 bg-emerald-50">
                  <CardContent className="p-4 text-center">
                    <Clock className="w-6 h-6 mx-auto text-emerald-600 mb-1" />
                    <p className="text-2xl font-bold text-emerald-700">{projectSummary.total_hours?.toFixed(1) || 0}</p>
                    <p className="text-xs text-emerald-600">Total Hours</p>
                  </CardContent>
                </Card>
                <Card className="border-0 bg-blue-50">
                  <CardContent className="p-4 text-center">
                    <DollarSign className="w-6 h-6 mx-auto text-blue-600 mb-1" />
                    <p className="text-2xl font-bold text-blue-700">${projectSummary.total_cost?.toFixed(2) || 0}</p>
                    <p className="text-xs text-blue-600">Total Cost</p>
                  </CardContent>
                </Card>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Card className="border-0 bg-stone-50">
                  <CardContent className="p-4 text-center">
                    <Users className="w-6 h-6 mx-auto text-stone-600 mb-1" />
                    <p className="text-2xl font-bold text-stone-700">{projectSummary.unique_employees || 0}</p>
                    <p className="text-xs text-stone-600">Employees</p>
                  </CardContent>
                </Card>
                <Card className="border-0 bg-stone-50">
                  <CardContent className="p-4 text-center">
                    <Clock className="w-6 h-6 mx-auto text-stone-600 mb-1" />
                    <p className="text-2xl font-bold text-stone-700">{projectSummary.total_entries || 0}</p>
                    <p className="text-xs text-stone-600">Time Entries</p>
                  </CardContent>
                </Card>
              </div>
              {isValidCoord(selectedProject?.latitude, selectedProject?.longitude) && (
                <div className="p-3 bg-emerald-50 rounded-lg text-sm">
                  <p className="font-medium text-emerald-700 flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    Geofence Location
                  </p>
                  <p className="text-emerald-600 text-xs mt-1">
                    {selectedProject.latitude.toFixed(6)}, {selectedProject.longitude.toFixed(6)} ({selectedProject.radius_meters || 150}m radius)
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
